import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import type { CSSProperties, RefObject } from "react"
import DeckCard from "@/components/DeckBrowser/DeckCard"
import PatternDemo from "@/components/DeckBrowser/PatternDemo"
import { morphClose, morphOpen } from "@/components/DeckBrowser/deckMorph"
import { cardFace, cardIdsForFamily, parseCardId } from "@/content/cards"
import { DECK_BROWSER_COPY, DECK_DETAIL_COPY } from "@/content/cardsRound"
import { FAMILIES } from "@/content/families"
import { patternsForCard } from "@/content/patterns"
import { pitfallForCard } from "@/content/pitfalls"
import type { CardId } from "@/types"
import styles from "@/styles/components/DeckDetail.module.css"

/*
 * The capability detail overlay — ported from the handoff's `.ov` / `.sheet`.
 *
 * Browser-only (renders `DeckCard`, never the in-session `CardFace`). It replaces
 * the old side panel: a pill with the capability name, the statement as the
 * headline, a "Be aware" pitfall, the card art, and "See it in action". Tech
 * stack and the old worked examples are intentionally gone.
 *
 * Opening and closing is the card morph (see `deckMorph`): a clone of the clicked
 * card grows from its on-screen rect into the sheet, flipping to the paper back,
 * and shrinks back on close. `sourceEl` is that card.
 *
 * The "Be aware" pitfall is live, from `content/pitfalls` (the re-paired v0
 * warnings), and "See it in action" from `content/patterns` (the Figma
 * card→pattern map plus the handoff's per-pattern copy), a `PatternDemo` each.
 */

const RM =
  typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches

// The card view's navigation arrows, reused for the overlay's side rails (see
// DeckCardView `AL`/`AR`).
const AL = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </svg>
)
const AR = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

interface DeckDetailProps {
  cardId: CardId
  /** Ref to the clicked card — the morph grows from and shrinks back into it. */
  sourceRef: RefObject<HTMLElement | null>
  onClose: () => void
}

export default function DeckDetail({ cardId, sourceRef, onClose }: DeckDetailProps) {
  // The card on show. Navigation stays internal to the overlay — the < >
  // controls step WITHIN the family (wrapping, like the card view) without
  // touching the stack behind, so the morph source stays the card we opened
  // from. `cardId` only seeds the first render: the overlay is a full-screen
  // modal and the views behind it are inert while it is up, so the prop never
  // changes under a mounted detail.
  const [current, setCurrent] = useState(cardId)

  const step = useCallback((dir: 1 | -1) => {
    setCurrent((at) => {
      const parsed = parseCardId(at)
      if (!parsed) return at
      const list = cardIdsForFamily(parsed.family)
      const i = list.indexOf(at)
      if (i < 0 || list.length < 2) return at
      return list[(i + dir + list.length) % list.length]
    })
  }, [])

  const face = cardFace(current)
  const family = parseCardId(current)?.family ?? 1
  // The family this capability belongs to — shown as a heading above the name
  // eyebrow so a card opened on its own still says which family it is from.
  const familyName = FAMILIES.find((entry) => entry.n === family)?.name
  const pitfall = pitfallForCard(current)
  const patterns = patternsForCard(current)

  const ovRef = useRef<HTMLDivElement>(null)
  const sheetRef = useRef<HTMLDivElement>(null)
  const closingRef = useRef(false)

  const palette = {
    "--fam-glow": `var(--family-${family}-deck-glow)`,
    "--fam-ink": `var(--family-${family}-deck-ink)`,
  } as CSSProperties

  // Open: grow the card into the sheet. Layout effect so the sheet is hidden
  // before paint (no flash of it at full size before the flight).
  useLayoutEffect(() => {
    const source = sourceRef.current
    if (RM || !source || !sheetRef.current) return
    morphOpen(source, sheetRef.current, () => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Close: shrink back into the card, fading the backdrop, then unmount.
  const close = () => {
    if (closingRef.current) return
    const source = sourceRef.current
    if (RM || !source || !sheetRef.current) {
      onClose()
      return
    }
    closingRef.current = true
    ovRef.current?.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 500,
      easing: "ease-in",
      fill: "forwards",
    })
    morphClose(source, sheetRef.current, onClose)
  }

  // Escape closes; the page behind must not scroll while the overlay is up.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close()
      else if (event.key === "ArrowLeft") step(-1)
      else if (event.key === "ArrowRight") step(1)
    }
    window.addEventListener("keydown", onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = previous
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Portalled to the body. The overlay is `position: fixed`, but it renders
  // inside a stacking context that sits BELOW the page chrome — AppShell's `.page`
  // (z-index 1) under `.chrome` (z-index 40), and the preview page's header does
  // the same. Trapped there, no z-index can lift it over the logo and nav: the
  // backdrop blurs the page but the mark floats on top. The portal moves it to
  // the root stacking context so it covers everything, chrome included. The morph
  // is unaffected — the sheet is viewport-fixed, so its coordinates don't move.
  return createPortal(
    // Clicking the blurred backdrop closes; clicks inside the sheet do not.
    <div
      className={styles.ov}
      style={palette}
      role="dialog"
      aria-modal="true"
      aria-label={face?.name}
      onClick={close}
      ref={ovRef}
    >
      <div className={styles.sheet} onClick={(event) => event.stopPropagation()} ref={sheetRef}>
        <button
          type="button"
          className={styles.close}
          onClick={close}
          aria-label={DECK_DETAIL_COPY.close}
        >
          <span aria-hidden="true">×</span>
        </button>

        <div className={styles.hero}>
          <div className={styles.heroText}>
            {familyName ? <p className={styles.family}>{familyName}</p> : null}
            <span className={styles.eyebrow}>{face?.name}</span>
            <h2 className={styles.statement}>{face?.statement}</h2>
          </div>
          <div className={styles.art}>
            <DeckCard cardId={current} />
          </div>
        </div>

        <section className={styles.mod}>
          {pitfall ? (
            <div className={styles.tldr}>
              <div className={styles.tldrItem}>
                <span className={styles.modPill}>{DECK_DETAIL_COPY.beAware}</span>
                <p>{pitfall.text}</p>
              </div>
            </div>
          ) : null}
          <span className={styles.modPill}>{DECK_DETAIL_COPY.inAction}</span>
          {patterns ? (
            <>
              <ul className={styles.patterns}>
                {patterns.patterns.map((pattern) => (
                  <li key={pattern.id} className={styles.pattern}>
                    <PatternDemo id={pattern.id} />
                    <div className={styles.patternHead}>
                      <span className={styles.patternName}>{pattern.name}</span>
                    </div>
                    <p className={styles.patternUse}>{pattern.use}</p>
                  </li>
                ))}
              </ul>
              {patterns.fit === "partial" && patterns.fitNote ? (
                <p className={styles.fitNote}>{patterns.fitNote}</p>
              ) : null}
            </>
          ) : (
            <h3 className={styles.modHeading}>{DECK_DETAIL_COPY.patternsComingSoon}</h3>
          )}
        </section>
      </div>

      {/* Side rails — step through the family's cards, like the card view's round
          Previous / Next. Children of the backdrop, not the sheet, so they stay
          viewport-fixed through the sheet's open/close morph; `stopPropagation`
          keeps a click on them from closing the overlay. */}
      <button
        type="button"
        className={`${styles.side} ${styles.sidePrev}`}
        aria-label={DECK_BROWSER_COPY.previous}
        onClick={(event) => {
          event.stopPropagation()
          step(-1)
        }}
      >
        <span className={`${styles.sideCircle} ${styles.sideLight}`}>{AL}</span>
      </button>
      <button
        type="button"
        className={`${styles.side} ${styles.sideNext}`}
        aria-label={DECK_BROWSER_COPY.next}
        onClick={(event) => {
          event.stopPropagation()
          step(1)
        }}
      >
        <span className={`${styles.sideCircle} ${styles.sideDark}`}>{AR}</span>
      </button>
    </div>,
    document.body
  )
}
